import {
  ApiClient,
  ApiClientContext,
  ApiClientOptions,
  RouteOptions,
  defaultAxiosConfig,
} from '@qualifio/api-client';
import { AxiosInstance } from 'axios';
import {
  AccessImpactAttributes,
  DeleteAccountRulesParams,
  EarnRulesExecutionResult,
  ImpactAttributes,
  ReplaceRulesBody,
  RuleAttributes,
  RuleInput,
  RulePositionChange,
  RuleType,
} from '.';
import { EarnEventAttributes } from '../ingestor-types';
import { ProfileAttributes, ProfileUpsert } from '../profile-api-client';

export interface GetRuleQueryParams {
  type?: RuleType;
  state?: 'ARCHIVED' | 'DELETED';
}

export interface GetAccountRulesQueryParams {
  withDeleted?: boolean;
}

export interface ExecuteEarnRulesQuery {
  programId?: string;
}

export class RuleApiClient extends ApiClient {
  static serviceName = 'rule-api';

  constructor(http?: AxiosInstance, context?: ApiClientContext, options?: ApiClientOptions) {
    super('/api/rule', http, context, options);
  }

  async executeBurnRules(
    programId: string,
    profile: ProfileAttributes,
    options?: RouteOptions,
  ): Promise<ImpactAttributes> {
    const { data } = await this.http.post(
      `/programs/${programId}/rules/burn/execute`,
      profile,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async executeEarnRules(
    accountId: string,
    programId: string | undefined,
    event: EarnEventAttributes,
    profile: ProfileAttributes,
    options?: RouteOptions,
  ): Promise<EarnRulesExecutionResult> {
    const { data } = await this.http.post(
      `/accounts/${accountId}/rules/earn/execute`,
      {
        eventSchema: event.payload,
        eventId: event.id,
        profile,
      },
      {
        ...defaultAxiosConfig(options),
        params: {
          ...options?.params,
          programId,
        } as ExecuteEarnRulesQuery,
      },
    );
    return data;
  }

  // TODO remove this method, use specific endpoints for each rule type
  async executeBurnRule(
    ruleId: string,
    profile: ProfileAttributes,
    options?: RouteOptions,
  ): Promise<ImpactAttributes> {
    const { data } = await this.http.post(
      `/rules/${ruleId}/burn/execute`,
      profile,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async executeAccessRule(
    programId: string,
    profile: ProfileUpsert,
    options?: RouteOptions,
  ): Promise<AccessImpactAttributes> {
    const { data } = await this.http.post(
      `/programs/${programId}/rule/access/execute`,
      profile,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async getRules(
    programId: string,
    queryParams: GetRuleQueryParams,
    options?: RouteOptions,
  ): Promise<RuleAttributes[]> {
    const { data } = await this.http.get(`/programs/${programId}/rules`, {
      ...defaultAxiosConfig(options),
      params: {
        ...options?.params,
        ...queryParams,
      },
    });
    return data;
  }

  async getAccountRules(
    accountId: string,
    options?: RouteOptions<GetAccountRulesQueryParams>,
  ): Promise<RuleAttributes[]> {
    const { data } = await this.http.get(
      `/accounts/${accountId}/rules`,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async deleteAccountRules(
    accountId: string,
    options?: RouteOptions<DeleteAccountRulesParams>,
  ): Promise<{ deleted: number }> {
    const { data } = await this.http.delete(
      `/accounts/${accountId}/rules`,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async addRule(
    programId: string,
    rule: RuleInput,
    options?: RouteOptions,
  ): Promise<RuleAttributes> {
    const { data } = await this.http.post(
      `/programs/${programId}/rules-v2`,
      rule,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async updateRule(
    ruleId: string,
    rule: RuleInput,
    options?: RouteOptions,
  ): Promise<RuleAttributes> {
    const { data } = await this.http.put(`/rules/${ruleId}`, rule, defaultAxiosConfig(options));
    return data;
  }

  async deleteRule(
    ruleId: string,
    version: number,
    options?: RouteOptions<{ force: boolean }>,
  ): Promise<void> {
    const { data } = await this.http.delete(`/rules/${ruleId}?version=${version}`, {
      ...defaultAxiosConfig(options),
      params: {
        ...options?.params,
        force: options?.params?.force,
      },
    });
    return data;
  }

  async updateRulesPosition(
    programId: string,
    changes: RulePositionChange[],
    options?: RouteOptions,
  ): Promise<void> {
    const { data } = await this.http.put(
      `/programs/${programId}/rules-position`,
      changes,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async replaceRules(
    programId: string,
    type: RuleType.EARN | RuleType.BURN,
    body: ReplaceRulesBody,
    options?: RouteOptions,
  ): Promise<RuleAttributes[]> {
    const { data } = await this.http.put(`/programs/${programId}/rules`, body, {
      ...defaultAxiosConfig(options),
      params: {
        ...options?.params,
        type,
      },
    });
    return data;
  }
}
